/**
 * A tela Pessoas (história 13.4, chamado de 21/10/2026, P13).
 *
 *     /pessoas?busca=&pagina=    todos os contatos que quem entrou enxerga
 *
 * No molde de `rotas/empresas.tsx`, e pelas mesmas razões: filha de `rotaDaAplicacao` (a guarda de
 * sessão é do layout), o caminho vem de `CAMINHOS`, e **a busca e a página moram na URL** — elas são
 * o "onde eu estou" de quem procura um contato, e em estado local se perderiam ao recarregar, ao
 * abrir a ficha de uma Empresa e voltar, e ao atravessar os 768px do shell.
 *
 * **A rota existe para todos os perfis.** Quem recorta é a RLS do `cadastro`: o Vendedor recebe as
 * Pessoas das Empresas que enxerga (FR-30) e só as Pessoas soltas que recebeu pelo WhatsApp (PN2).
 */

import { createRoute } from '@tanstack/react-router'

import { ListaDePessoas } from '@/modulos/cadastro/ListaDePessoas'
import { PAGINA_MAXIMA, TAMANHO_DA_BUSCA } from '@/modulos/cadastro/api'
import { CAMINHOS } from '@/shell/navegacao'
import { rotaDaAplicacao } from './aplicacao'
import { PRIMEIRA_PAGINA, etiquetaDaUrl } from './empresas'

/** O que a URL desta tela carrega. Os dois campos são opcionais: `/pessoas` cru é válido. */
export interface BuscaDaListaDePessoas {
  busca?: string
  pagina?: number
  /** A etiqueta da equipe do filtro (13.6). */
  etiqueta?: string
}

function Pessoas() {
  const { busca, pagina, etiqueta }: BuscaDaListaDePessoas = rotaDePessoas.useSearch()
  const navegar = rotaDePessoas.useNavigate()

  return (
    <ListaDePessoas
      busca={busca ?? ''}
      pagina={pagina ?? PRIMEIRA_PAGINA}
      etiqueta={etiqueta}
      /* Digitar **substitui** a entrada de histórico e zera a página; paginar **empurra** — o
         mesmo par de intenções da tela de Empresas. */
      aoTrocarABusca={(termo) => {
        void navegar({
          search: (atual) => ({
            ...atual,
            busca: termo === '' ? undefined : termo,
            pagina: undefined,
          }),
          // O *state* do aviso de apagar (13.6, `usoDaEtiqueta`) atravessa a digitação e a página.
          state: true,
          replace: true,
        })
      }}
      aoTrocarAEtiqueta={(etiquetaId) => {
        void navegar({
          search: (atual) => ({ ...atual, etiqueta: etiquetaId, pagina: undefined }),
        })
      }}
      aoTrocarAPagina={(destino, opcoes) => {
        void navegar({
          search: (atual) => ({
            ...atual,
            pagina: destino === PRIMEIRA_PAGINA ? undefined : destino,
          }),
          state: true,
          replace: opcoes?.substituir === true,
        })
      }}
    />
  )
}

export const rotaDePessoas = createRoute({
  getParentRoute: () => rotaDaAplicacao,
  path: CAMINHOS.pessoas,
  component: Pessoas,
  /** A URL é entrada de quem chega: o que não for um par válido cai fora aqui, como em Empresas. */
  validateSearch: (parametros: Record<string, unknown>): BuscaDaListaDePessoas => {
    const termo = typeof parametros.busca === 'string' ? parametros.busca : ''
    const pagina = Number(parametros.pagina)
    return {
      etiqueta: etiquetaDaUrl(parametros.etiqueta),
      busca: termo === '' ? undefined : termo.slice(0, TAMANHO_DA_BUSCA),
      pagina:
        Number.isSafeInteger(pagina) && pagina > PRIMEIRA_PAGINA && pagina <= PAGINA_MAXIMA
          ? pagina
          : undefined,
    }
  },
})
